import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  PriceProtectionEnum,
  type Counterparty,
  type Customer,
  type CustomerConfiguration,
  type MarketAccount,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import { freeze } from 'immer';
import { OrderStrategiesEnum } from '../../tokens/orderStrategy';
import type { MarketSelectorPreset } from '../MarketSelector/types';
import {
  MARKET_SELECTION_ASYNC_REFDATA_TIMEOUT_MS,
  type AdjustedSettlementDatesReferenceData,
  type BasketReferenceData,
  type CurrenciesRefData,
  type FixingIndicesReferenceData,
  type FundDefinitionReferenceData,
  type MarketAccountPermissionFiltersRefData,
  type MarketAccountReferenceData,
  type MarketsReferenceData,
  type OMSReferenceDataState,
  type OMSSettingsState,
  type OptionRefData,
  type OrderPresetsRefData,
  type SecuritiesReferenceData,
  type StrategiesReferenceData,
} from './types';

export const initialRefDataState: OMSReferenceDataState = {
  settings: {
    showAllInPrices: null,
    showDelta1BPS: false,
    defaultPriceProtection: PriceProtectionEnum.Error,
    lastStrategyUsed: OrderStrategiesEnum.Limit,
    useTradeAllocations: false,
    defaultOrderStrategy: null,
    defaultMultilegOrderStrategy: null,
    alwaysCheckPriceReasonability: true,
    enableDerivativeContractDefault: false,
    enableGroups: false,
    supportedStrategiesForPricingModes: ['Limit', 'Iceberg'],
    enableRFQFormFundSecurities: false,
    enableRFQFormOptions: false,
    enableRFQSizeCounteringMultitokenCompound: false,
    enableRFQSizeCounteringSpot: false,
    enableOptionTrading: false,
    enableNegotiatedRFQFlow: false,
    allowSyntheticCcy: false,
    allowSyntheticCrosses: false,
    exclusivelyPrimePriceOnRePriming: false,
    enableReduceOnly: false,
    enableReduceFirst: false,
    ddhDefaultsByID: new Map(),
    enableTakerMarketplaceETFRFQFlow: false,
    truncateCareOrderID: false,
    defaultToEmptyOrderMarkets: false,
    defaultMarketAccounts: {},
    defaultAggregationId: undefined,
    enableAccountSegregation: false,
    resetOrderAccountsOnSubAccountChange: false,
    enableMSSValidation: false,
    marketSelectionAsyncRefDataTimeoutMs: MARKET_SELECTION_ASYNC_REFDATA_TIMEOUT_MS,
    disableNonSTFNegotiatedRFQ: false,
    disableUnifiedLiquidityDerivativesMarketSelectionRestriction: false,
    disableCrossCurrencyQty: false,
    nonSpotDelta1DefaultQty: 'Base',
    fundCareOrderDefaultSubAccounts: undefined,
    useFundDefinitionAndTradableIndexesStreams: false,
    supportAdjustedSettlementDate: false,
    defaultBasketSettleOffsetOverride: undefined,
    enableRFQFormFundField: false,
    enableDurationSubmission: false,
    rfqFormTradingProtocol: undefined,
  },
  securities: {
    securitiesList: [],
    securitiesBySymbol: new Map(),
    futures: [],
    perps: [],
    perpsByCurrencyPair: new Map(),
    futuresByCurrencyPair: new Map(),
    futuresByMarketByCurrency: new Map(),
    isReady: false,
  },
  marketAccounts: {
    marketAccountsByMarket: new Map(),
    marketAccountsList: [],
    marketAccountsByName: new Map(),
    isReady: false,
  },
  customers: [],
  customerMarketAccounts: [],
  customerConfigurations: [],
  strategies: {
    strategiesByName: new Map(),
    strategiesList: [],
    ddhStrategiesList: [],
    isReady: false,
  },
  markets: {
    marketsList: [],
    marketsByName: new Map(),
    isReady: false,
  },
  currencies: {
    currenciesBySymbol: new Map(),
    isReady: false,
  },
  options: {
    options: [],
    expirationByMarketByCurrencyIdentity: new Map(),
    optionSecurityBySymbol: new Map(),
    strikesByOptionKey: new Map(),
    isReady: false,
  },
  orderPresets: {
    orderPresetsList: [],
    orderPresetsByID: new Map(),
    isReady: false,
  },
  unifiedLiquidityTokens: new Map(),
  fixingIndices: {
    fixingIndicesList: [],
    fixingIndicesByName: new Map(),
    tradableIndicesBySymbol: new Map(),
    marketsByFixingIndex: new Map(),
    isReady: false,
  },
  marketAccountPermissionFilters: {
    marketAccountFilterIDsBySubAccountName: new Map(),
    marketAccountPermissionFiltersByFilterID: new Map(),
    isReady: false,
  },
  counterparties: [],
  baskets: {
    basketsList: [],
  },
  funds: {
    fundsBySymbol: new Map(),
    fundsList: [],
  },
  adjustedSettlementDates: {
    settlementDates: [],
  },
  tradingAggregationPresets: undefined,
};

// Re freeze() calls. Freeze is useful for setting large data sets. It wraps the passed in data set in an immer wrapper, which lets
// immer just check that that wrapper was not modified / written into, instead of having to check each member of the data set individually.
// https://immerjs.github.io/immer/performance/#pre-freeze-data
const referenceDataSlice = createSlice({
  name: 'referenceData',
  initialState: initialRefDataState,
  reducers: {
    setDefaultSubAccount: (state, action: PayloadAction<string | undefined>) => {
      state.defaultSubAccount = action.payload;
    },
    setSecurities: (state, action: PayloadAction<SecuritiesReferenceData>) => {
      state.securities = freeze(action.payload);
    },
    setMarketAccounts: (state, action: PayloadAction<MarketAccountReferenceData>) => {
      state.marketAccounts = freeze(action.payload);
    },
    setStrategies: (state, action: PayloadAction<StrategiesReferenceData>) => {
      state.strategies = action.payload;
    },
    setMarkets: (state, action: PayloadAction<MarketsReferenceData>) => {
      state.markets = action.payload;
    },
    setOptions: (state, action: PayloadAction<OptionRefData>) => {
      state.options = freeze(action.payload);
    },
    setCurrencies: (state, action: PayloadAction<CurrenciesRefData>) => {
      state.currencies = freeze(action.payload);
    },
    setCustomers: (state, action: PayloadAction<Customer[]>) => {
      state.customers = freeze(action.payload);
    },
    setCounterparties: (state, action: PayloadAction<Counterparty[]>) => {
      state.counterparties = freeze(action.payload);
    },
    setCustomerMarketAccounts: (state, action: PayloadAction<MarketAccount[]>) => {
      state.customerMarketAccounts = freeze(action.payload);
    },
    setCustomerConfigurations: (state, action: PayloadAction<CustomerConfiguration[]>) => {
      state.customerConfigurations = freeze(action.payload);
    },
    setOMSSettings: (state, action: PayloadAction<OMSSettingsState>) => {
      state.settings = action.payload;
    },
    setOrderPresets: (state, action: PayloadAction<OrderPresetsRefData>) => {
      state.orderPresets = action.payload;
    },
    setUnifiedLiquidityTokens: (state, action: PayloadAction<Map<string, UnifiedLiquidityResponse>>) => {
      state.unifiedLiquidityTokens = freeze(action.payload);
    },
    setFixingIndices: (state, action: PayloadAction<FixingIndicesReferenceData>) => {
      state.fixingIndices = action.payload;
    },
    setMarketAccountPermissionFilters: (state, action: PayloadAction<MarketAccountPermissionFiltersRefData>) => {
      state.marketAccountPermissionFilters = freeze(action.payload);
    },
    setBaskets: (state, action: PayloadAction<BasketReferenceData>) => {
      state.baskets = action.payload;
    },
    setFunds: (state, action: PayloadAction<FundDefinitionReferenceData>) => {
      state.funds = action.payload;
    },
    setAdjustedSettlementDates: (state, action: PayloadAction<AdjustedSettlementDatesReferenceData>) => {
      state.adjustedSettlementDates = action.payload;
    },
    setTradingAggregationPresets: (state, action: PayloadAction<MarketSelectorPreset[] | undefined>) => {
      state.tradingAggregationPresets = action.payload;
    },
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      /**
       * You should not be using this other than from tests
       */
      return action.payload;
    },
  },
});

export const {
  setDefaultSubAccount,
  setSecurities,
  setCustomers,
  setCounterparties,
  setMarketAccounts,
  setCustomerMarketAccounts,
  setCustomerConfigurations,
  setStrategies,
  setMarkets,
  setOptions,
  setCurrencies,
  setOrderPresets,
  setReferenceData,
  setOMSSettings,
  setUnifiedLiquidityTokens,
  setFixingIndices,
  setMarketAccountPermissionFilters,
  setBaskets,
  setFunds,
  setAdjustedSettlementDates,
  setTradingAggregationPresets,
} = referenceDataSlice.actions;

export const referenceDataSliceReducer = referenceDataSlice.reducer;
